/**
 * Voice Service - OpenAI Realtime API Integration
 * 
 * Provides speech-to-speech conversational AI using OpenAI's Realtime API.
 * Uses WebRTC for low-latency browser communication.
 * 
 * Architecture:
 * - Browser WebRTC → OpenAI Realtime API (via /v1/realtime/calls GA endpoint)
 * - Native speech-to-speech (no separate STT/TTS)
 * - ~200-300ms latency
 * 
 * @author Heseda AI Team
 * @version 1.0
 */

import { getApiBaseUrl, nativeFetch } from '../utils/apiConfig';
import { getAuthHeaders } from './apiAuth';
import { isInPhase1, PHASE_1_THRESHOLD } from '../utils/phaseUtils';

// Model is configured server-side in api/src/functions/getRealtimeToken.ts
// and embedded in the ephemeral token — no model param needed on the client.

// Types for OpenAI Realtime API
// Supported voices for OpenAI Realtime API (as of Dec 2024)
export type RealtimeVoice = 'alloy' | 'ash' | 'ballad' | 'coral' | 'echo' | 'sage' | 'shimmer' | 'verse' | 'marin' | 'cedar';

export interface VoiceSessionConfig {
  language: string;
  systemPrompt: string;
  voice?: RealtimeVoice;
}

export interface VoiceSessionState {
  status: 'disconnected' | 'connecting' | 'connected' | 'listening' | 'speaking' | 'error';
  error?: string;
  transcript?: string;
  aiResponse?: string;
}

export interface VoiceSessionEvents {
  onStateChange: (state: VoiceSessionState) => void;
  onSpeechStart?: () => void; // Called when user starts speaking (for message ordering)
  onTranscript: (text: string, isFinal: boolean) => void;
  onAIResponse: (text: string, isFinal: boolean) => void;
  onError: (error: string) => void;
  onInactivityTimeout?: () => void; // Called when session auto-closes due to inactivity
}

/**
 * OpenAI Realtime Voice Session
 * Manages WebRTC connection to OpenAI's Realtime API
 */
// Client-side inactivity timeout (60 seconds - auto-close session)
const INACTIVITY_TIMEOUT_MS = 60000;

// Server-side VAD idle timeout (OpenAI max is 30000ms)
const VAD_IDLE_TIMEOUT_MS = 30000;

// Connection timeout — abort if WebRTC doesn't connect within this window
const CONNECTION_TIMEOUT_MS = 10000;

// Client-side silence detection for Android fallback (server VAD may not work in WebView)
const ANDROID_SILENCE_THRESHOLD = 0.01; // Audio level below this is considered silence
const ANDROID_SILENCE_DURATION_MS = 2000; // 2 seconds of silence to trigger end of speech
const ANDROID_AUDIO_CHECK_INTERVAL_MS = 100; // Check audio level every 100ms

/**
 * Map app language code to ISO 639-1 for Whisper transcription.
 * Whisper needs an explicit language hint to avoid misdetecting short utterances
 * (e.g. English "search product" transcribed as Japanese "サーチしたプロダクト").
 * Exported for unit testing.
 */
export function toWhisperLanguage(language: string): string {
  // pt-BR → pt; all others (en, ko, es) are already ISO 639-1
  return language.split('-')[0];
}

/**
 * Build a language-enforcement instruction for the Realtime session.
 * Covers all languages reachable via REGION_CONFIG.hl values.
 * Exported for unit testing.
 */
export function getLanguageInstruction(language: string): string {
  switch (language) {
    case 'ko':
      return 'IMPORTANT: You MUST respond ONLY in Korean (한국어). Never use English, Hindi, or any other language. 반드시 한국어로만 대답하세요.';
    case 'en':
      return 'IMPORTANT: You MUST respond ONLY in English. Never switch to another language unless the user explicitly asks.';
    case 'pt-BR':
      return 'IMPORTANT: You MUST respond ONLY in Brazilian Portuguese (Português do Brasil). Nunca responda em outro idioma.';
    case 'es':
      return 'IMPORTANT: You MUST respond ONLY in Spanish (Español). Nunca respondas en otro idioma.';
    case 'de':
      return 'IMPORTANT: You MUST respond ONLY in German (Deutsch). Antworten Sie ausschließlich auf Deutsch.';
    case 'fr':
      return 'IMPORTANT: You MUST respond ONLY in French (Français). Répondez uniquement en français.';
    case 'it':
      return 'IMPORTANT: You MUST respond ONLY in Italian (Italiano). Rispondi esclusivamente in italiano.';
    case 'nl':
      return 'IMPORTANT: You MUST respond ONLY in Dutch (Nederlands). Antwoord uitsluitend in het Nederlands.';
    case 'sv':
      return 'IMPORTANT: You MUST respond ONLY in Swedish (Svenska). Svara enbart på svenska.';
    case 'pl':
      return 'IMPORTANT: You MUST respond ONLY in Polish (Polski). Odpowiadaj wyłącznie po polsku.';
    default:
      return 'IMPORTANT: Respond in the same language the user speaks. If the user switches languages, switch with them.';
  }
}

/**
 * Build the session.update payload sent over the data channel.
 * Exported for unit testing — the shape MUST match the GA Realtime API schema.
 *
 * IMPORTANT: Do NOT add `temperature`, `max_tokens`, or other parameters that
 * are not in the GA session.update schema. The API will reject unknown fields.
 * See: https://developers.openai.com/api/reference/resources/realtime/client-events
 */
export function buildRealtimeSessionConfig(config: VoiceSessionConfig): Record<string, unknown> {
  const defaultVoice: RealtimeVoice = config.language === 'ko' ? 'shimmer' : 'alloy';
  const languageInstruction = getLanguageInstruction(config.language);

  return {
    type: 'session.update',
    session: {
      type: 'realtime',
      instructions: `${languageInstruction}\n\n${config.systemPrompt}`,
      audio: {
        input: {
          format: { type: 'audio/pcm', rate: 24000 },
          transcription: {
            model: 'whisper-1',
            language: toWhisperLanguage(config.language),
          },
          turn_detection: {
            type: 'server_vad',
            // iOS WKWebView mic levels are typically lower; use a more sensitive threshold
            threshold: typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent) ? 0.5 : 0.65,
            prefix_padding_ms: 500,
            silence_duration_ms: 1000,
            create_response: true,
            idle_timeout_ms: VAD_IDLE_TIMEOUT_MS,
          },
        },
        output: {
          format: { type: 'audio/pcm', rate: 24000 },
          voice: config.voice || defaultVoice,
        },
      },
    },
  };
}

export class VoiceSession {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private audioElement: HTMLAudioElement | null = null;
  private mediaStream: MediaStream | null = null;
  private events: VoiceSessionEvents;
  private config: VoiceSessionConfig;
  private state: VoiceSessionState = { status: 'disconnected' };
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private lastActivityTime: number = Date.now();
  
  // Android silence detection fallback
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private silenceStartTime: number | null = null;
  private audioCheckInterval: ReturnType<typeof setInterval> | null = null;
  private isAndroid: boolean = false;
  private connectionTimer: ReturnType<typeof setTimeout> | null = null;
  private connectAborted = false;

  constructor(config: VoiceSessionConfig, events: VoiceSessionEvents) {
    this.config = config;
    this.events = events;
    // Detect Android for fallback silence detection
    this.isAndroid = /Android/.test(navigator.userAgent);
  }

  /**
   * Connect to OpenAI Realtime API via WebRTC
   */
  async connect(): Promise<void> {
    try {
      this.updateState({ status: 'connecting' });

      // Abort if connection doesn't succeed within CONNECTION_TIMEOUT_MS
      this.connectAborted = false;
      this.connectionTimer = setTimeout(() => {
        if (this.state.status === 'connecting') {
          this.connectAborted = true;
          this.disconnect();
          const msg = 'Connection timed out. Please try again.';
          this.updateState({ status: 'error', error: msg });
          this.events.onError(msg);
        }
      }, CONNECTION_TIMEOUT_MS);

      // console.log('[VoiceService] Starting connection...');

      // Get ephemeral API key from our backend (secure)
      // console.log('[VoiceService] Step 1: Getting ephemeral key...');
      const ephemeralKey = await this.getEphemeralKey();
      if (this.connectAborted) return; // Timeout fired — bail out
      // console.log('[VoiceService] Step 1: Got ephemeral key');

      // Request microphone access
      if (import.meta.env.DEV) console.log('[VoiceService] Step 2: Requesting microphone access...');
      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          }
        });
      } catch (micError) {
        const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
        const errObj = micError as { name?: string; message?: string };
        const errName = errObj?.name ?? 'Unknown';
        const errMsg = errObj?.message ?? String(micError);
        console.error('[VoiceService] Microphone access failed:', errName, errMsg);
        if (isIOS && (errName === 'NotAllowedError' || errName === 'NotFoundError')) {
          throw new Error('Microphone access denied. Please go to Settings > Heseda and enable Microphone.');
        }
        throw new Error(`Microphone access failed: ${errMsg}`);
      }
      if (this.connectAborted) return; // Timeout fired — bail out
      if (import.meta.env.DEV) console.log('[VoiceService] Step 2: Microphone access granted');

      // Set up Android silence detection fallback (server VAD may not work in Android WebView)
      if (this.isAndroid) {
        this.setupAndroidSilenceDetection();
      }

      // Create peer connection
      // console.log('[VoiceService] Step 3: Creating RTCPeerConnection...');
      this.pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      });

      // Add audio track from microphone
      this.mediaStream.getAudioTracks().forEach(track => {
        this.pc!.addTrack(track, this.mediaStream!);
      });
      // console.log('[VoiceService] Step 3: Peer connection created, audio tracks added');

      // Set up audio output with robust autoplay handling
      this.audioElement = new Audio();
      this.audioElement.autoplay = true;
      // playsInline helps with mobile browsers
      (this.audioElement as any).playsInline = true;
      // Some browsers need the element in DOM to play
      this.audioElement.style.display = 'none';
      document.body.appendChild(this.audioElement);
      
      this.pc.ontrack = (event) => {
        // console.log('🔊 Received audio track from OpenAI, streams:', event.streams.length);
        if (this.audioElement && event.streams[0]) {
          this.audioElement.srcObject = event.streams[0];
          // Explicitly try to play (handles autoplay policy)
          this.audioElement.play()
            .then(() => console.log('🔊 Audio playback started'))
            .catch((err) => {
              console.warn('Audio autoplay blocked:', err.message);
            });
        }
      };

      // Create data channel for events
      this.dc = this.pc.createDataChannel('oai-events');
      this.setupDataChannel();

      // Create and set local offer
      // console.log('[VoiceService] Step 4: Creating SDP offer...');
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);
      if (this.connectAborted) return; // Timeout fired — bail out
      // console.log('[VoiceService] Step 4: SDP offer created');

      // Send offer to OpenAI GA WebRTC signaling endpoint and get answer
      // GA endpoint: /v1/realtime/calls (model embedded in ephemeral token)
      // console.log('[VoiceService] Step 5: Sending SDP to OpenAI...');
      const sdpResponse = await fetch(
        'https://api.openai.com/v1/realtime/calls',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${ephemeralKey}`,
            'Content-Type': 'application/sdp',
          },
          body: offer.sdp,
        }
      );

      if (!sdpResponse.ok) {
        const errorText = await sdpResponse.text().catch(() => 'Unknown');
        let statusHint = '';
        switch (sdpResponse.status) {
          case 400:
            statusHint = 'Invalid request - please try reconnecting.';
            break;
          case 401:
            statusHint = 'Authentication failed - please check your API key.';
            break;
          case 403:
            statusHint = 'Access denied - your account may not have permission to use the Realtime API.';
            break;
          case 500:
            statusHint = 'Server error - please try again in a moment.';
            break;
        }
        console.error('[VoiceService] Step 5 FAILED:', sdpResponse.status, statusHint || errorText);
        const hintSuffix = statusHint ? ` (${statusHint})` : '';
        throw new Error(`OpenAI Realtime API error: ${sdpResponse.status}${hintSuffix} - ${errorText}`);
      }
      // console.log('[VoiceService] Step 5: Got SDP answer from OpenAI');

      const answerSdp = await sdpResponse.text();
      if (this.connectAborted) return; // Timeout fired — bail out
      await this.pc.setRemoteDescription({
        type: 'answer',
        sdp: answerSdp,
      });
      // console.log('[VoiceService] Step 6: Remote description set, connection established');

      // Connection succeeded — cancel the timeout
      if (this.connectionTimer) {
        clearTimeout(this.connectionTimer);
        this.connectionTimer = null;
      }

      this.updateState({ status: 'connected' });

      // Start inactivity timer
      this.resetInactivityTimer();

      // Send initial session configuration
      this.sendSessionConfig();

    } catch (error) {
      if (this.connectionTimer) {
        clearTimeout(this.connectionTimer);
        this.connectionTimer = null;
      }
      const errorMsg = error instanceof Error ? error.message : 'Connection failed';
      this.updateState({ status: 'error', error: errorMsg });
      this.events.onError(errorMsg);
      throw error;
    }
  }

  /**
   * Disconnect and clean up resources
   */
  disconnect(): void {
    if (this.dc) {
      this.dc.close();
      this.dc = null;
    }

    if (this.pc) {
      this.pc.close();
      this.pc = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.srcObject = null;
      // Remove from DOM if we added it
      if (this.audioElement.parentNode) {
        this.audioElement.parentNode.removeChild(this.audioElement);
      }
      this.audioElement = null;
    }

    // Clear inactivity timer
    this.clearInactivityTimer();

    // Clear connection timeout
    if (this.connectionTimer) {
      clearTimeout(this.connectionTimer);
      this.connectionTimer = null;
    }
    
    // Clean up Android silence detection
    this.cleanupAndroidSilenceDetection();

    this.updateState({ status: 'disconnected' });
  }

  /**
   * Reset inactivity timer - called on any user/AI activity
   */
  private resetInactivityTimer(): void {
    this.lastActivityTime = Date.now();
    this.clearInactivityTimer();
    
    // Only set timer if connected
    if (this.isConnected()) {
      this.inactivityTimer = setTimeout(() => {
        // console.log('[VoiceService] Session timeout due to inactivity (60s)');
        this.events.onInactivityTimeout?.();
        // Only disconnect if still connected (timeout handler may have already disconnected)
        if (this.isConnected()) {
          this.disconnect();
        }
      }, INACTIVITY_TIMEOUT_MS);
    }
  }

  /**
   * Clear the inactivity timer
   */
  private clearInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  /**
   * Set up client-side silence detection for Android
   * This is a fallback because server VAD may not work reliably in Android WebView
   */
  private setupAndroidSilenceDetection(): void {
    if (!this.mediaStream) return;
    
    try {
      // Create audio context to analyze microphone input
      this.audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      
      const source = this.audioContext.createMediaStreamSource(this.mediaStream);
      source.connect(this.analyser);
      
      const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
      
      // console.log('[VoiceService] Android silence detection initialized');
      
      // Check audio levels periodically
      this.audioCheckInterval = setInterval(() => {
        if (!this.analyser || !this.isConnected()) return;
        
        this.analyser.getByteFrequencyData(dataArray);
        
        // Calculate average audio level (0-1 range)
        const average = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length / 255;
        
        if (average < ANDROID_SILENCE_THRESHOLD) {
          // Silence detected
          if (!this.silenceStartTime) {
            this.silenceStartTime = Date.now();
          } else if (Date.now() - this.silenceStartTime > ANDROID_SILENCE_DURATION_MS) {
            // Prolonged silence - reset the timer
            // The server VAD should handle this, but if not, log it
            // console.log('[VoiceService] Android: Detected prolonged silence');
            this.silenceStartTime = null;
          }
        } else {
          // Audio detected - reset silence timer and update activity
          this.silenceStartTime = null;
          this.resetInactivityTimer();
        }
      }, ANDROID_AUDIO_CHECK_INTERVAL_MS);
      
    } catch (error) {
      console.warn('[VoiceService] Failed to set up Android silence detection:', error);
    }
  }

  /**
   * Clean up Android silence detection resources
   */
  private cleanupAndroidSilenceDetection(): void {
    if (this.audioCheckInterval) {
      clearInterval(this.audioCheckInterval);
      this.audioCheckInterval = null;
    }
    
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
    
    this.analyser = null;
    this.silenceStartTime = null;
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.state.status === 'connected' || 
           this.state.status === 'listening' || 
           this.state.status === 'speaking';
  }

  /**
   * Send a text message (for hybrid text+voice mode)
   */
  sendTextMessage(text: string): void {
    if (!this.dc || this.dc.readyState !== 'open') {
      console.warn('Data channel not open');
      return;
    }

    const event = {
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [{ type: 'input_text', text }],
      },
    };
    this.dc.send(JSON.stringify(event));

    // Trigger response
    this.dc.send(JSON.stringify({ type: 'response.create' }));
  }

  /**
   * Get ephemeral API key from our backend
   * Passes session configuration to ensure language is set from the start
   */
  private async getEphemeralKey(): Promise<string> {
    const apiUrl = `${getApiBaseUrl()}/getRealtimeToken`;
    // console.log('[VoiceService] Fetching ephemeral key from:', apiUrl);
    
    try {
      const authHeaders = await getAuthHeaders();
      // console.log('[VoiceService] Auth headers obtained, has token:', !!authHeaders['X-Firebase-Auth']);
      
      // Use nativeFetch to bypass CORS on native platforms (iOS/Android)
      const response = await nativeFetch(apiUrl, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          language: this.config.language,
          instructions: this.config.systemPrompt,
          voice: this.config.voice || (this.config.language === 'ko' ? 'shimmer' : 'alloy'),
        }),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error('[VoiceService] Token fetch failed:', response.status, errorText);
        throw new Error(`Failed to get realtime token: ${response.status}`);
      }

      const data = await response.json();
      // console.log('[VoiceService] Token received successfully, language:', this.config.language);
      return data.client_secret?.value || data.value || data.token;
    } catch (error) {
      console.error('[VoiceService] getEphemeralKey error:', error);
      // Provide more specific error message
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to reach API server. Check your internet connection.');
      }
      throw error;
    }
  }

  /**
   * Set up data channel event handlers
   */
  private setupDataChannel(): void {
    if (!this.dc) return;

    this.dc.onopen = () => {
      console.log('🎤 Realtime data channel opened');
    };

    this.dc.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleRealtimeEvent(data);
      } catch (e) {
        console.warn('Failed to parse realtime event:', e);
      }
    };

    this.dc.onerror = (error) => {
      console.error('Data channel error:', error);
      this.events.onError('Data channel error');
    };

    this.dc.onclose = () => {
      console.log('Data channel closed');
      this.updateState({ status: 'disconnected' });
    };
  }

  /**
   * Handle events from OpenAI Realtime API
   */
  private handleRealtimeEvent(event: any): void {
    switch (event.type) {
      case 'session.created':
        console.log('🎤 Realtime session created');
        break;

      case 'session.updated':
        console.log('🎤 Session updated');
        break;

      case 'input_audio_buffer.speech_started':
        this.updateState({ status: 'listening' });
        this.resetInactivityTimer(); // User activity - reset timeout
        // Notify that user started speaking (for message ordering)
        this.events.onSpeechStart?.();
        break;

      case 'input_audio_buffer.speech_stopped':
        // User stopped speaking - also counts as activity (they were just speaking)
        this.resetInactivityTimer();
        break;

      case 'conversation.item.input_audio_transcription.completed':
        // User's speech transcribed
        const userTranscript = event.transcript || '';
        this.events.onTranscript(userTranscript, true);
        this.updateState({ ...this.state, transcript: userTranscript });
        this.resetInactivityTimer(); // User activity - reset timeout
        break;

      case 'response.output_audio_transcript.delta':
        // AI response text delta (GA event name)
        const delta = event.delta || '';
        this.events.onAIResponse(delta, false);
        this.resetInactivityTimer(); // AI activity - reset timeout
        break;

      case 'response.output_audio_transcript.done':
        // AI response complete (GA event name)
        const fullTranscript = event.transcript || '';
        console.log('🔊 AI response transcript complete:', fullTranscript.substring(0, 50) + '...');
        this.events.onAIResponse(fullTranscript, true);
        this.updateState({ ...this.state, aiResponse: fullTranscript });
        break;

      case 'response.output_text.delta':
        // Text-only response delta (GA event name; fallback when model responds without audio)
        const textDelta = event.delta || '';
        this.events.onAIResponse(textDelta, false);
        this.resetInactivityTimer();
        break;

      case 'response.output_text.done':
        // Text-only response complete (GA event name; fallback when model responds without audio)
        const fullText = event.text || '';
        console.log('🔊 AI text response complete:', fullText.substring(0, 50) + '...');
        this.events.onAIResponse(fullText, true);
        this.updateState({ ...this.state, aiResponse: fullText });
        break;

      case 'response.output_audio.delta':
        // AI speaking - audio data is being sent (GA event name)
        if (this.state.status !== 'speaking') {
          console.log('🔊 AI started speaking (audio stream)');
        }
        this.updateState({ status: 'speaking' });
        break;

      case 'response.output_audio.done':
        // AI finished speaking (GA event name)
        console.log('🔊 AI finished speaking');
        this.updateState({ status: 'connected' });
        break;

      case 'response.done':
        // Response complete
        this.updateState({ status: 'connected' });
        break;

      case 'error':
        console.error('Realtime API error:', event.error);
        this.events.onError(event.error?.message || 'Unknown error');
        break;

      default:
        // Log unknown events for debugging (skip known informational events)
        if (event.type && 
            !event.type.startsWith('response.output_audio.') &&
            !['response.content_part.done', 'response.content_part.added',
              'response.output_item.done', 'response.output_item.added',
              'rate_limits.updated', 'conversation.item.created',
              'conversation.item.added', 'conversation.item.done'].includes(event.type)) {
          console.log('🎤 Realtime event:', event.type);
        }
    }
  }

  /**
   * Build a language-enforcement instruction based on the user's language preference.
   * For ko/en/pt-BR/es we use explicit native-language enforcement (proven to reduce language drift).
   * For unsupported languages the fallback instructs the model to match the user's spoken language.
   */
  private getLanguageInstruction(): string {
    return getLanguageInstruction(this.config.language);
  }

  /**
   * Send initial session configuration
   * Note: Primary language/instructions are set when creating the token on backend.
   * This reinforces settings and adds turn detection configuration.
   */
  private sendSessionConfig(): void {
    if (!this.dc || this.dc.readyState !== 'open') {
      // Wait for data channel to open
      this.dc?.addEventListener('open', () => this.sendSessionConfig(), { once: true });
      return;
    }

    const sessionConfig = buildRealtimeSessionConfig(this.config);

    console.log('[VoiceService] Sending session config, language:', this.config.language);
    this.dc.send(JSON.stringify(sessionConfig));
  }

  /**
   * Update AI context with search results
   * Call this after a search completes so AI knows about the products
   */
  updateSearchContext(searchResults: Array<{
    name: string;
    link?: string;
    price?: string | number;
    reason?: string;
  }>): void {
    if (!this.dc || this.dc.readyState !== 'open') {
      console.warn('[VoiceService] Cannot update search context - data channel not open');
      return;
    }

    if (!searchResults || searchResults.length === 0) {
      return;
    }

    // Build context string for AI
    const isKorean = this.config.language === 'ko';
    const contextHeader = isKorean
      ? '방금 검색한 제품 목록입니다. 사용자가 "첫번째", "두번째" 등으로 물어보면 이 목록을 참고하세요:'
      : 'Here are the products from the recent search. When user asks about "first", "second", etc., refer to this list:';

    const productList = searchResults.slice(0, 5).map((product, index) => {
      const num = index + 1;
      const ordinal = isKorean
        ? `${num}번째`
        : num === 1 ? '1st' : num === 2 ? '2nd' : num === 3 ? '3rd' : `${num}th`;
      
      let desc = `${ordinal}: ${product.name}`;
      if (product.price) desc += ` (${product.price})`;
      if (product.reason) desc += ` - ${product.reason}`;
      return desc;
    }).join('\n');

    const contextUpdate = {
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'system',
        content: [
          {
            type: 'input_text',
            text: `${contextHeader}\n\n${productList}`,
          },
        ],
      },
    };

    console.log('[VoiceService] Updating search context with', searchResults.length, 'products');
    this.dc.send(JSON.stringify(contextUpdate));
  }

  /**
   * Update and broadcast state
   */
  private updateState(newState: Partial<VoiceSessionState>): void {
    this.state = { ...this.state, ...newState };
    this.events.onStateChange(this.state);
  }
}

/**
 * Check if voice features are supported in current environment
 * 
 * Requirements:
 * - RTCPeerConnection for WebRTC
 * - navigator.mediaDevices.getUserMedia for microphone access
 * 
 * iOS Notes:
 * - Requires NSMicrophoneUsageDescription in Info.plist
 * - WKWebView supports getUserMedia since iOS 14.3+
 * - Some Capacitor WebView configurations may block this
 * 
 * Android Notes:
 * - Requires RECORD_AUDIO permission in AndroidManifest.xml
 * - Requires MODIFY_AUDIO_SETTINGS for echo cancellation
 * - Runtime permission prompt is handled by Capacitor WebView
 */
export function isVoiceSupported(): boolean {
  const hasRTC = typeof RTCPeerConnection !== 'undefined';
  const hasMediaDevices = typeof navigator !== 'undefined' && 
    navigator.mediaDevices !== undefined;
  const hasGetUserMedia = hasMediaDevices && 
    typeof navigator.mediaDevices.getUserMedia === 'function';
  
  // Detect platform
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isCapacitor = !!(window as unknown as { Capacitor?: unknown }).Capacitor;
  
  // Debug logging for mobile troubleshooting
  if (isCapacitor) {
    console.log('[VoiceService] isVoiceSupported check:', {
      hasRTC,
      hasMediaDevices,
      hasGetUserMedia,
      userAgent: navigator.userAgent,
      isIOS,
      isAndroid,
      isCapacitor,
      platform: isIOS ? 'iOS' : isAndroid ? 'Android' : 'Web',
    });
  }
  
  return hasRTC && hasGetUserMedia;
}

/**
 * Search result summary for voice context
 * Matches the format used by text AI in chatPrompts.ts
 */
export interface VoiceSearchContext {
  recentSearches: Array<{
    name: string;
    link?: string;
    source?: 'inhouse' | 'search' | 'web';
    price?: string | number;
    reason?: string;
  }>;
  lastSearchKeywords?: string[];
}

/**
 * Build system prompt for voice mode based on profile
 * 
 * This mirrors the text AI prompt building in api/src/prompts/chatPrompts.ts
 * to ensure consistent behavior between text and voice modes.
 * 
 * @param language - User's language preference
 * @param masterProfile - User's profile data
 * @param profileCompletion - Profile completion percentage
 * @param searchContext - Optional recent search results from Firebase
 */
export function buildVoiceSystemPrompt(
  language: string,
  masterProfile: any,
  profileCompletion: number,
  searchContext?: VoiceSearchContext
): string {
  // Phase 1 is the ONLY phase where search is blocked
  // Once user exits Phase 1 (>= PHASE_1_THRESHOLD%), search becomes available regardless of future phases
  const isPhase1 = isInPhase1(profileCompletion);
  
  const basePrompt = buildBasePromptForLanguage(language, profileCompletion, isPhase1);

  // Add profile context if available
  const useKorean = language === 'ko';
  let profileContext = '';
  if (masterProfile?.skin?.type) {
    profileContext += useKorean 
      ? `\n사용자 피부 타입: ${masterProfile.skin.type}`
      : `\nUser skin type: ${masterProfile.skin.type}`;
  }
  if (masterProfile?.skin?.concerns?.length > 0) {
    profileContext += useKorean
      ? `\n주요 고민: ${masterProfile.skin.concerns.join(', ')}`
      : `\nMain concerns: ${masterProfile.skin.concerns.join(', ')}`;
  }

  // Add search context if available (mirrors text AI behavior from chatPrompts.ts)
  let searchContextSection = '';
  if (searchContext?.recentSearches && searchContext.recentSearches.length > 0) {
    const searchList = searchContext.recentSearches.slice(0, 5).map((item, i) => {
      const num = i + 1;
      const sourceLabel = item.source === 'inhouse' ? '⭐ Heseda' : '🔍';
      const priceInfo = item.price ? ` [${item.price}]` : '';
      return `${num}. [${sourceLabel}] ${item.name}${priceInfo}`;
    }).join('\n');

    searchContextSection = useKorean
      ? `\n\n🔍 사용자의 최근 검색 결과:\n${searchList}\n\n서수 표현 이해:\n- "첫번째", "1번" → 1번 제품\n- "두번째", "2번" → 2번 제품\n- "세번째", "3번" → 3번 제품\n\n참고: 가격은 사용자가 물어볼 때만 말해주세요. 제품 설명 시 먼저 언급하지 마세요.`
      : `\n\n🔍 User's recent search results:\n${searchList}\n\nOrdinal understanding:\n- "first one", "1st" → product #1\n- "second one", "2nd" → product #2\n- "third one", "3rd" → product #3\n\nNote: Only mention price when user asks. Do not proactively include it in product descriptions.`;
  }

  return basePrompt + profileContext + searchContextSection;
}

/**
 * Build the language-specific base prompt for voice.
 * Korean and English have full native prompts; es / pt-BR get localized intros
 * with English-based search rules (structurally identical).
 */
function buildBasePromptForLanguage(language: string, profileCompletion: number, isPhase1: boolean): string {
  if (language === 'ko') {
    return `당신은 헤세다(Heseda)입니다 — 사용자의 개인 스킨케어 친구이자 전문 컨설턴트입니다. '헤세다'는 히브리어로 '변함없는 사랑'을 뜻하는 이름입니다 — 그 따뜻한 사랑을 담아 대화해주세요. 음성으로 대화하고 있으므로 자연스럽고 친근하게 말해주세요.
답변은 짧고 명확하게 해주세요 (1-3문장). 너무 길게 설명하지 마세요. 진심으로 사랑하는 친구처럼 대화하세요.
⚠️ 절대 자기소개를 반복하지 마세요. "나는 헤세다야", "변함없는 사랑을 뜻하는" 같은 말을 매번 하지 마세요. 사용자가 "누구야?"라고 물으면 짧게 한 줄로 답하고 대화를 이어가세요.

🧴 전문 영역 — 스킨케어 전용:
- 당신은 스킨케어, 화장품, 뷰티 제품 전문가입니다.
- 세럼, 모이스처라이저, 클렌저, 선크림, 마스크팩, 토너 등 스킨케어 제품만 다룹니다.
- 스킨케어와 관련 없는 질문 (가전제품, 음식, 기술 등)에는 정중히 응답하세요: "저는 스킨케어 전문이에요! 피부 관련 질문이 있으시면 도와드릴게요."
- 비 스킨케어 제품을 절대 추천하지 마세요.

⚠️ 건강 관련 안내:
- 일반적인 스킨케어 정보와 제품 제안만 제공합니다.
- 의료 전문가가 아닙니다. 심한 피부 증상 시: "피부과 전문의와 상담하시기를 권합니다"라고 안내하세요.

현재 프로필 완성도: ${profileCompletion}%
${isPhase1 
  ? `⚠️ Phase 1 - 제품 검색 기능은 아직 사용할 수 없습니다.

중요 - 검색 요청 대응:
- 사용자가 "검색해줘", "추천해줘", "찾아줘" 등을 말하면, 친절하게 설명해주세요: "제품 검색은 프로필 완성도 ${PHASE_1_THRESHOLD}%에서 열려요. 지금은 ${profileCompletion}% 완성됐어요. 프로필을 조금만 더 완성하면 맞춤 추천을 받을 수 있어요!"
- 절대 "제품을 검색할게요"라고 말하지 마세요 - 아직 검색할 수 없습니다.
- 대신 피부 타입, 고민, 루틴 등 프로필 정보를 수집하세요.`
  : `✅ 검색 기능 활성화 - 제품 검색 기능을 사용할 수 있습니다.

중요 - 제품 검색 응답 규칙:
- 사용자가 명확하게 "검색해줘", "추천해줘", "찾아줘" 등을 말했을 때만 "제품을 검색할게요"라고 응답하세요.
- 음성이 불분명하거나 배경 소음 같다면, 검색 의도로 해석하지 마세요. 대신 "죄송해요, 잘 못 들었어요. 다시 말씀해 주시겠어요?"라고 물어보세요.
- 확실하지 않으면 절대 "제품을 검색할게요"라고 말하지 마세요. 검색 기능은 시스템이 실제 텍스트를 확인한 후에만 작동합니다.`}`;
  }

  if (language === 'es') {
    return `Eres Heseda — una consultora experta de cuidado de la piel y amiga personal del usuario. Estás hablando por voz, así que sé natural y conversacional.
Mantén tus respuestas concisas (1-3 oraciones). Habla como una amiga cercana y cálida — no como un asistente de IA genérico.

🧴 DOMINIO — Solo cuidado de la piel:
- Eres experto en skincare, cosméticos y productos de belleza: serums, hidratantes, limpiadores, protector solar, mascarillas, tónicos, etc.
- Si el usuario pregunta sobre algo no relacionado con skincare (electrodomésticos, comida, tecnología), responde amablemente: "¡Soy especialista en cuidado de la piel! ¿Tienes alguna pregunta sobre tu piel?"
- NUNCA recomiendes productos que no sean de skincare o belleza.

⚠️ Aviso de salud: Solo ofreces información general de skincare y sugerencias de productos. Para condiciones severas, recomienda ver a un dermatólogo.

Perfil completado: ${profileCompletion}%
${buildSearchRulesEN(profileCompletion, isPhase1)}`;
  }

  if (language === 'pt-BR') {
    return `Você é Heseda — uma consultora especialista em cuidados com a pele e amiga pessoal do usuário. Você está falando por voz, então seja natural e conversacional.
Mantenha suas respostas concisas (1-3 frases). Fale como uma amiga próxima e carinhosa — não como um assistente de IA genérico.

🧴 DOMÍNIO — Apenas cuidados com a pele:
- Você é especialista em skincare, cosméticos e produtos de beleza: séruns, hidratantes, limpadores, protetor solar, máscaras, tônicos, etc.
- Se o usuário perguntar sobre algo não relacionado a skincare (eletrodomésticos, comida, tecnologia), responda gentilmente: "Sou especialista em cuidados com a pele! Tem alguma pergunta sobre sua pele?"
- NUNCA recomende produtos que não sejam de skincare ou beleza.

⚠️ Aviso de saúde: Você oferece apenas informações gerais de skincare e sugestões de produtos. Para condições graves, recomende consultar um dermatologista.

Perfil completo: ${profileCompletion}%
${buildSearchRulesEN(profileCompletion, isPhase1)}`;
  }

  // English (default for 'en' and any unknown language)
  return `You are Heseda — an expert skincare consultant and the user's personal skincare friend. Your name means "steadfast love" (from Hebrew Hesed) — embody that warmth in every word. You are speaking via voice, so be natural and conversational.
Keep your responses concise (1-3 sentences). Don't be verbose. Talk like a warm, knowledgeable friend who genuinely cares — not a generic AI assistant.

🧴 DOMAIN — Skincare Only:
- You are an expert in skincare, cosmetics, and beauty products: serums, moisturizers, cleansers, sunscreen, masks, toners, essences, exfoliants, etc.
- If user asks about non-skincare topics (kitchen appliances, food, technology, etc.), politely redirect: "I'm a skincare specialist! Do you have any questions about your skin?"
- NEVER recommend non-skincare products. Stay strictly within cosmetics and beauty.

⚠️ Health Disclaimer:
- You provide general skincare information and product suggestions only.
- You are NOT a medical professional. For severe skin conditions, recommend seeing a dermatologist.

Current profile completion: ${profileCompletion}%
${buildSearchRulesEN(profileCompletion, isPhase1)}`;
}

/**
 * English-language search rules reused by en, es, and pt-BR prompts.
 * (The model understands English instructions regardless of response language.)
 */
function buildSearchRulesEN(profileCompletion: number, isPhase1: boolean): string {
  if (isPhase1) {
    return `⚠️ Phase 1 - Product search is NOT available yet.

IMPORTANT - Search Request Handling:
- If user asks to search or recommend products, kindly explain: "Product search unlocks at ${PHASE_1_THRESHOLD}% profile completion. You're currently at ${profileCompletion}%. Complete your profile a bit more and you'll get personalized recommendations!"
- NEVER claim you are starting a product search - search is not available yet.
- Instead, continue gathering profile information through conversation.`;
  }
  return `✅ Search Available - Product search is enabled.

IMPORTANT - Product Search Response Rules:
- ONLY confirm that you are starting a product search when the user CLEARLY asks to search or recommend products.
- If audio is unclear or sounds like background noise, do NOT interpret it as a search request. Instead ask the user to repeat.
- When in doubt, do NOT claim you are searching for products. The search function only works when the system verifies the actual text matches search patterns.`;
}
